import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { track } from "@/lib/pixel";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";

import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AssessmentPage from "@/pages/AssessmentPage";
import StudentDashboard from "@/pages/StudentDashboard";
import CounsellorDashboard from "@/pages/CounsellorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ChatbotPage from "@/pages/ChatbotPage";
import CollegePredictorPage from "@/pages/CollegePredictorPage";
import RankPredictorPage from "@/pages/RankPredictorPage";
import CollegeComparePage from "@/pages/CollegeComparePage";
import CounsellorMarketplace from "@/pages/CounsellorMarketplace";
import PricingPage from "@/pages/PricingPage";
import CommunityPage from "@/pages/CommunityPage";
import ScholarshipsPage from "@/pages/ScholarshipsPage";
import ReferralsPage from "@/pages/ReferralsPage";
import LegalPage, { ContactPage } from "@/pages/LegalPage";
import RecommendedCollegesPage from "@/pages/RecommendedCollegesPage";
import AdminCollegesPage from "@/pages/AdminCollegesPage";
import CareerGraphPage from "@/pages/CareerGraphPage";
import ParentDashboard from "@/pages/ParentDashboard";
import NotFoundPage from "@/pages/NotFoundPage";
import AboutPage from "@/pages/AboutPage";
import PublicReportPage from "@/pages/PublicReportPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import EzHandoffPage from "@/pages/EzHandoffPage";
import PrivacyCentrePage from "@/pages/PrivacyCentrePage";
import GuardianConsentPage from "@/pages/GuardianConsentPage";
import AdminPrivacyPage from "@/pages/AdminPrivacyPage";
import AdminMentorsPage from "@/pages/AdminMentorsPage";
import PrepHome from "@/pages/prep/PrepHome";
import PrepExamHub from "@/pages/prep/PrepExamHub";
import PrepTests from "@/pages/prep/PrepTests";
import PrepAttempt from "@/pages/prep/PrepAttempt";
import PrepResult from "@/pages/prep/PrepResult";
import PrepPerformance from "@/pages/prep/PrepPerformance";
import PrepHistory from "@/pages/prep/PrepHistory";
import MockTestLanding from "@/pages/prep/MockTestLanding";
import SubjectLanding from "@/pages/prep/SubjectLanding";
import AdminTestPrepPage from "@/pages/AdminTestPrepPage";
import { ConsentBanner } from "@/components/ConsentBanner";
import { initConsent } from "@/lib/consent";

const RoleDashboard = () => {
  const { user } = useAuth();
  if (user?.role === "admin") return <AdminDashboard />;
  if (user?.role === "counsellor") return <CounsellorDashboard />;
  if (user?.role === "parent") return <ParentDashboard />;
  return <StudentDashboard />;
};

const Shell = ({ children, roles }) => (
  <ProtectedRoute roles={roles}>
    <DashboardLayout>{children}</DashboardLayout>
  </ProtectedRoute>
);

function RouteTracker() {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    // Skip the very first render — index.html already fires the initial PageView.
    if (first.current) { first.current = false; return; }
    track("PageView");
  }, [location.pathname]);
  return null;
}

initConsent();

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <ConsentBanner />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/r/:token" element={<PublicReportPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/ez" element={<EzHandoffPage />} />
            <Route path="/guardian-consent" element={<GuardianConsentPage />} />
            <Route path="/dashboard/privacy" element={<Shell><PrivacyCentrePage /></Shell>} />
            <Route path="/admin/privacy" element={<Shell roles={["admin"]}><AdminPrivacyPage /></Shell>} />
            <Route path="/admin/mentors" element={<Shell roles={["admin"]}><AdminMentorsPage /></Shell>} />
            <Route path="/ez-admin-x9f3q7" element={<AdminLoginPage />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/refund" element={<LegalPage type="refund" />} />
            <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/dashboard" element={<Shell><RoleDashboard /></Shell>} />
            <Route path="/assessment" element={<Shell roles={["student"]}><AssessmentPage /></Shell>} />
            <Route path="/chat" element={<Shell roles={["student"]}><ChatbotPage /></Shell>} />
            <Route path="/predictor/college" element={<Shell roles={["student"]}><CollegePredictorPage /></Shell>} />
            <Route path="/predictor/rank" element={<Shell roles={["student"]}><RankPredictorPage /></Shell>} />
            <Route path="/compare" element={<Shell roles={["student"]}><CollegeComparePage /></Shell>} />
            <Route path="/scholarships" element={<Shell roles={["student"]}><ScholarshipsPage /></Shell>} />
            <Route path="/apply-colleges" element={<Shell roles={["student"]}><RecommendedCollegesPage /></Shell>} />
            <Route path="/career-graph" element={<Shell roles={["student"]}><CareerGraphPage /></Shell>} />
            <Route path="/admin/colleges" element={<Shell roles={["admin"]}><AdminCollegesPage /></Shell>} />
            <Route path="/prep" element={<Shell roles={["student"]}><PrepHome /></Shell>} />
            <Route path="/prep/exam/:examId" element={<Shell roles={["student"]}><PrepExamHub /></Shell>} />
            <Route path="/prep/exam/:examId/tests" element={<Shell roles={["student"]}><PrepTests /></Shell>} />
            <Route path="/prep/test/:attemptId" element={<Shell roles={["student"]}><PrepAttempt /></Shell>} />
            <Route path="/prep/result/:attemptId" element={<Shell roles={["student"]}><PrepResult /></Shell>} />
            <Route path="/prep/performance" element={<Shell roles={["student"]}><PrepPerformance /></Shell>} />
            <Route path="/prep/history" element={<Shell roles={["student"]}><PrepHistory /></Shell>} />
            <Route path="/admin/test-prep" element={<Shell roles={["admin"]}><AdminTestPrepPage /></Shell>} />
            <Route path="/mock-test/:slug" element={<MockTestLanding />} />
            <Route path="/mock-test/:slug/:subject" element={<SubjectLanding />} />
            <Route path="/prep/:slug" element={<MockTestLanding />} />
            <Route path="/prep/:slug/:subject" element={<SubjectLanding />} />
            <Route path="/referrals" element={<Shell roles={["student"]}><ReferralsPage /></Shell>} />
            <Route path="/counsellors" element={<Shell><CounsellorMarketplace /></Shell>} />
            <Route path="/pricing" element={<Shell roles={["student"]}><PricingPage /></Shell>} />
            <Route path="/community" element={<Shell><CommunityPage /></Shell>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
